import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useLanguage } from '../LanguageContext';
import ShuftiVerification from '../components/ShuftiVerification';
import {
  LEGAL_FORMS, ID_DOC_TYPES, PURPOSES, PAYMENT_METHODS,
  SOURCES_OF_FUNDS, HOW_HEARD, COUNTRIES, SANCTIONS_LINK
} from '../constants/kyc';
import { getRegions } from '../constants/regions';
import {
  isValidSiTaxNumber, isValidSiRegistrationNumber,
  isValidSiPostalCode, isSlovenia,
  isValidDisplayDate, displayToIso, isoToDisplay, maskDateInput,
  isValidNationalId, isValidEuVat, countryIso
} from '../utils/kycValidators';
import './Verification.css';

// Only ex-Yugoslav countries require personal ID (EMŠO/JMBG).
const EX_YUGOSLAV_COUNTRIES = ['SI', 'HR', 'BA', 'RS', 'ME', 'MK'];

function isExYugoslav(citizenship) {
  return EX_YUGOSLAV_COUNTRIES.includes(countryIso(citizenship));
}

// Which fields belong to each step (used for "Next"/submit validation).
const STEP_FIELDS = {
  1: (f) => f.is_legal_entity
    ? ['company_name', 'legal_form', 'company_tax_number', 'company_registration_number']
    : ['first_name', 'last_name'],
  2: (f) => {
    const fields = ['address_street', 'address_postal_code', 'address_city', 'birth_date', 'tax_number'];
    if (isExYugoslav(f.citizenship)) fields.push('emso');
    return fields;
  },
  3: () => ['id_doc_number', 'id_doc_issuer', 'id_doc_issue_date', 'id_doc_expiry_date'],
  4: () => ['buy_volume_per_year', 'purpose', 'source_of_funds', 'payment_method'],
};

// Validate a single field against the current form. Returns '' when valid.
// `t` is passed in explicitly because this is a module-level function
// (outside the component) and has no access to the LanguageContext.
function validateField(field, value, f, t) {
  const text = typeof value === 'string' ? value.trim() : (value == null ? '' : value);
  const requiredMsg = t('errors.REQUIRED_FIELD');
  switch (field) {
    case 'first_name':
    case 'last_name':
    case 'company_name':
    case 'address_street':
    case 'address_city':
    case 'id_doc_number':
    case 'id_doc_issuer':
      return text ? '' : requiredMsg;
    case 'legal_form':
    case 'purpose':
    case 'payment_method':
      return value ? '' : requiredMsg;
    case 'birth_date':
      if (!text) return requiredMsg;
      if (!isValidDisplayDate(value)) return t('verification.common.invalidDateError');
      if (displayToIso(value) > new Date().toISOString().slice(0, 10)) return t('verification.common.futureDateError');
      return '';
    case 'id_doc_issue_date':
      if (!text) return requiredMsg;
      return isValidDisplayDate(value) ? '' : t('verification.common.invalidDateError');
    case 'company_tax_number':
      if (!text) return requiredMsg;
      return isValidEuVat(value, countryIso(f.company_register_country)) ? '' : t('verification.step1.invalidTaxNumberError');
    case 'company_registration_number':
      if (!text) return '';
      return isValidSiRegistrationNumber(value) ? '' : t('verification.step1.invalidRegistrationNumberError');
    case 'address_postal_code':
      if (!text) return requiredMsg;
      if (isSlovenia(f.address_country) && !isValidSiPostalCode(value)) return t('verification.common.invalidPostalCodeError');
      return '';
    case 'tax_number':
      if (!text) return requiredMsg;
      return isValidSiTaxNumber(value) ? '' : t('verification.step2.invalidTaxNumberError');
    case 'emso':
      if (!isExYugoslav(f.citizenship)) return '';
      if (!text) return requiredMsg;
      return isValidNationalId(value, countryIso(f.citizenship)) ? '' : t('verification.step2.invalidPersonalIdError');
    case 'id_doc_expiry_date':
      if (f.id_doc_permanent) return '';
      if (!text) return t('verification.step3.expiryDateRequiredError');
      return isValidDisplayDate(value) ? '' : t('verification.common.invalidDateError');
    case 'buy_volume_per_year':
      if (text === '') return requiredMsg;
      return parseFloat(value) > 0 ? '' : t('verification.step4.invalidAmountError');
    case 'source_of_funds':
      return (Array.isArray(value) && value.length > 0) ? '' : t('verification.step4.sourceOfFundsRequiredError');
    default:
      return '';
  }
}

const STEP_TITLES = [
  { labelKey: 'verification.step1.title' },
  { labelKey: 'verification.step2.title' },
  { labelKey: 'verification.step3.indicatorTitle' },
  { labelKey: 'verification.step4.title' },
];

const emptyOwner = () => ({ full_name: '', birth_date: '', ownership_share: '', address: '', is_pep: false });

function Verification() {
  const { user, authFetch } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPepHelp, setShowPepHelp] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});

  const [form, setForm] = useState({
    is_legal_entity: false,
    // individual
    first_name: '',
    last_name: '',
    // legal entity
    company_name: '',
    legal_form: '',
    company_tax_number: '',
    company_registration_number: '',
    company_register_country: 'Slovenia',
    phone: '',
    // address
    address_street: '',
    address_postal_code: '',
    address_city: '',
    address_region: '',
    address_country: 'Slovenia',
    has_temp_address: false,
    temp_address_street: '',
    temp_address_postal_code: '',
    temp_address_city: '',
    temp_address_country: 'Slovenia',
    // birth
    birth_date: '',
    birthplace: '',
    birth_country: 'Slovenia',
    // tax/id
    tax_number: '',
    emso: '',
    // citizenship
    citizenship: 'Slovenia',
    dual_citizenship: false,
    dual_citizenship_country: '',
    sanctions_connection: false,
    // shufti
    shufti_reference: '',
    // id document
    id_doc_type: 'osebna',
    id_doc_number: '',
    id_doc_issuer: '',
    id_doc_issue_date: '',
    id_doc_expiry_date: '',
    id_doc_permanent: false,
    // pep
    pep_status: false,
    pep_details: '',
    // transaction context
    buy_volume_per_year: '',
    purpose: '',
    purpose_other: '',
    source_of_funds: [],
    source_of_funds_other: '',
    payment_method: '',
    recommendation_from: '',
    heard_about_us: '',
    heard_about_us_other: '',
    customer_comment: ''
  });

  const [owners, setOwners] = useState([emptyOwner()]);

  // Redirect customers who already submitted KYC away from this page.
  useEffect(() => {
    if (user && user.status && user.status !== 'incomplete') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Update a field and, if it is already showing an error, re-validate live so
  // the message clears as soon as the value becomes valid.
  const setField = (field, value) => {
    set(field, value);
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: validateField(field, value, { ...form, [field]: value }, t) };
    });
  };

  // Validate a field when the user leaves it.
  const blurField = (field) => {
    setFieldErrors(prev => ({ ...prev, [field]: validateField(field, form[field], form, t) }));
  };

  const fieldError = (name) => fieldErrors[name]
    ? <span className="field-error">{fieldErrors[name]}</span>
    : null;
  const errClass = (name) => (fieldErrors[name] ? 'input-error' : '');

  const toggleSource = (value) => {
    setForm(prev => {
      const current = prev.source_of_funds;
      const next = current.includes(value)
        ? current.filter(s => s !== value)
        : [...current, value];
      return { ...prev, source_of_funds: next };
    });
    // Clear the "vir sredstev" error once at least one is selected.
    setFieldErrors(prev => prev.source_of_funds ? { ...prev, source_of_funds: '' } : prev);
  };

  const updateOwner = (idx, field, value) => {
    setOwners(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };
  const addOwner = () => setOwners(prev => [...prev, emptyOwner()]);
  const removeOwner = (idx) => setOwners(prev => prev.filter((_, i) => i !== idx));

  // Validate all fields belonging to a step; highlight any that fail.
  const validateStepFields = (n) => {
    const fields = STEP_FIELDS[n] ? STEP_FIELDS[n](form) : [];
    const errs = {};
    for (const fld of fields) {
      const msg = validateField(fld, form[fld], form, t);
      if (msg) errs[fld] = msg;
    }
    // Check if source_proof document is required (step 4)
    if (n === 4 && isSourceProofRequired() && !uploadedDocs.source_proof) {
      errs.source_proof = t('verification.step4.sourceProofRequiredError');
    }
    setFieldErrors(prev => ({ ...prev, ...errs }));
    if (Object.keys(errs).length > 0) {
      setError(t('verification.common.fixHighlightedFieldsError'));
      return false;
    }
    setError('');
    return true;
  };

  const next = () => { if (validateStepFields(step)) setStep(step + 1); };
  const back = () => { setStep(step - 1); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStepFields(step)) return;

    setLoading(true);
    setError('');

    const payload = { ...form };
    // Convert dd/mm/yyyy display dates to ISO yyyy-mm-dd for the DATE columns.
    ['birth_date', 'id_doc_issue_date', 'id_doc_expiry_date'].forEach(fld => {
      payload[fld] = displayToIso(form[fld]) || null;
    });
    if (form.is_legal_entity) {
      payload.beneficial_owners = owners
        .filter(o => o.full_name)
        .map(o => ({ ...o, birth_date: displayToIso(o.birth_date) || null }));
    }

    try {
      const res = await authFetch('/api/auth/kyc', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('verification.common.saveError'));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload a document via multipart. doc_type: source_proof | register_extract | other
  const uploadDocument = async (file, docType) => {
    const token = localStorage.getItem('auth_token');
    const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3004';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    const res = await fetch(`${apiUrl}/api/auth/kyc-documents`, {
      method: 'POST',
      headers: { 'X-Session-Token': token },
      body: fd
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || t('verification.common.uploadError'));
    }
    return res.json();
  };

  const [uploadMsg, setUploadMsg] = useState('');
  const handleFileUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadMsg(t('verification.common.uploadingMessage'));
    try {
      await uploadDocument(file, docType);
      setUploadMsg(t('verification.common.uploadedMessage', { filename: file.name }));
      setUploadedDocs(prev => ({ ...prev, [docType]: true }));
      setFieldErrors(prev => ({ ...prev, [docType]: '' }));
    } catch (err) {
      setUploadMsg(err.message);
    }
  };

  const email = user?.email || '';

  // Check if proof of source of funds document is required
  const isSourceProofRequired = () => {
    const buyVolume = form.buy_volume_per_year ? parseFloat(form.buy_volume_per_year) : 0;
    return buyVolume >= 15000 || form.pep_status;
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <h1>{t('verification.common.pageTitle')}</h1>
        <p>{t('verification.common.pageSubtitle')}</p>
      </div>

      {/* Step indicator */}
      <div className="kyc-steps">
        {STEP_TITLES.map((s, i) => (
          <div key={i} className={`kyc-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'completed' : ''}`}>
            <span className="kyc-step-num">{step > i + 1 ? '✓' : i + 1}</span>
            <span className="kyc-step-title">{t(s.labelKey)}</span>
          </div>
        ))}
      </div>

      <form className="kyc-form" onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}

        {/* Step 1: Subject & identity */}
        {step === 1 && (
          <div className="kyc-section">
            <h2>{t('verification.step1.title')}</h2>

            <div className="form-field">
              <label>{t('verification.step1.legalEntityQuestion')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input type="radio" checked={!form.is_legal_entity}
                    onChange={() => set('is_legal_entity', false)} /> {t('verification.step1.individualOption')}
                </label>
                <label className="radio-label">
                  <input type="radio" checked={form.is_legal_entity}
                    onChange={() => set('is_legal_entity', true)} /> {t('verification.step1.legalEntityOption')}
                </label>
              </div>
            </div>

            {!form.is_legal_entity ? (
              <div className="form-grid">
                <div className="form-field">
                  <label>{t('verification.step1.firstNameLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.first_name} className={errClass('first_name')}
                    onChange={(e) => setField('first_name', e.target.value)}
                    onBlur={() => blurField('first_name')} />
                  {fieldError('first_name')}
                </div>
                <div className="form-field">
                  <label>{t('verification.step1.lastNameLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.last_name} className={errClass('last_name')}
                    onChange={(e) => setField('last_name', e.target.value)}
                    onBlur={() => blurField('last_name')} />
                  {fieldError('last_name')}
                </div>
              </div>
            ) : (
              <>
                <div className="form-grid">
                  <div className="form-field">
                    <label>{t('verification.step1.companyNameLabel')} <span className="required">*</span></label>
                    <input type="text" value={form.company_name} className={errClass('company_name')}
                      onChange={(e) => setField('company_name', e.target.value)}
                      onBlur={() => blurField('company_name')} />
                    {fieldError('company_name')}
                  </div>
                  <div className="form-field">
                    <label>{t('verification.step1.legalFormLabel')} <span className="required">*</span></label>
                    <select value={form.legal_form} className={errClass('legal_form')}
                      onChange={(e) => setField('legal_form', e.target.value)}
                      onBlur={() => blurField('legal_form')}>
                      <option value="">{t('verification.common.selectPlaceholder')}</option>
                      {LEGAL_FORMS.map(f => <option key={f.value} value={f.value}>{t(f.labelKey)}</option>)}
                    </select>
                    {fieldError('legal_form')}
                  </div>
                  <div className="form-field">
                    <label>{t('verification.step1.companyTaxNumberLabel')} <span className="required">*</span></label>
                    <input type="text" value={form.company_tax_number} className={errClass('company_tax_number')}
                      onChange={(e) => setField('company_tax_number', e.target.value)}
                      onBlur={() => blurField('company_tax_number')}
                      placeholder="SI12345678" />
                    {fieldError('company_tax_number')}
                  </div>
                  <div className="form-field">
                    <label>{t('verification.step1.registrationNumberLabel')}</label>
                    <input type="text" value={form.company_registration_number} className={errClass('company_registration_number')}
                      onChange={(e) => setField('company_registration_number', e.target.value)}
                      onBlur={() => blurField('company_registration_number')} />
                    {fieldError('company_registration_number')}
                  </div>
                  <div className="form-field">
                    <label>{t('verification.step1.registerCountryLabel')}</label>
                    <select value={form.company_register_country}
                      onChange={(e) => set('company_register_country', e.target.value)}>
                      {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                    </select>
                  </div>
                </div>

                {!isSlovenia(form.company_register_country) && (
                  <div className="form-field">
                    <label>{t('verification.step1.registerExtractLabel')}</label>
                    <input type="file" onChange={(e) => handleFileUpload(e, 'register_extract')} />
                    {uploadMsg && <span className="field-hint">{uploadMsg}</span>}
                  </div>
                )}

                <div className="kyc-subsection">
                  <h3>{t('verification.step1.beneficialOwnersHeading')}</h3>
                  {owners.map((owner, idx) => (
                    <div key={idx} className="owner-row">
                      <div className="form-grid">
                        <div className="form-field">
                          <label>{t('verification.step1.ownerFullNameLabel')}</label>
                          <input type="text" value={owner.full_name}
                            onChange={(e) => updateOwner(idx, 'full_name', e.target.value)} />
                        </div>
                        <div className="form-field">
                          <label>{t('verification.common.birthDateLabel')}</label>
                          <input type="text" inputMode="numeric" value={owner.birth_date}
                            onChange={(e) => updateOwner(idx, 'birth_date', maskDateInput(e.target.value))}
                            placeholder={t('verification.common.dateFormatPlaceholder')} maxLength={10} />
                        </div>
                        <div className="form-field">
                          <label>{t('verification.step1.ownershipShareLabel')}</label>
                          <input type="number" min="0" max="100" value={owner.ownership_share}
                            onChange={(e) => updateOwner(idx, 'ownership_share', e.target.value)} />
                        </div>
                        <div className="form-field">
                          <label>{t('verification.step1.ownerAddressLabel')}</label>
                          <input type="text" value={owner.address}
                            onChange={(e) => updateOwner(idx, 'address', e.target.value)} />
                        </div>
                      </div>
                      <label className="checkbox-label">
                        <input type="checkbox" checked={owner.is_pep}
                          onChange={(e) => updateOwner(idx, 'is_pep', e.target.checked)} /> PEP
                      </label>
                      {owners.length > 1 && (
                        <button type="button" className="btn-text" onClick={() => removeOwner(idx)}>
                          {t('verification.step1.removeOwnerButton')}
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary" onClick={addOwner}>
                    {t('verification.step1.addOwnerButton')}
                  </button>
                </div>
              </>
            )}

            <div className="form-field">
              <label>{t('verification.step1.phoneLabel')}</label>
              <input type="tel" value={form.phone}
                onChange={(e) => set('phone', e.target.value)} placeholder="+386 XX XXX XXX" />
            </div>
          </div>
        )}

        {/* Step 2: Address & birth */}
        {step === 2 && (
          <div className="kyc-section">
            <h2>{t('verification.step2.title')}</h2>

            <div className="kyc-subsection">
              <h3>{t('verification.step2.permanentAddressHeading')}</h3>
              <div className="form-grid">
                <div className="form-field full-width">
                  <label>{t('verification.common.streetLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.address_street} className={errClass('address_street')}
                    onChange={(e) => setField('address_street', e.target.value)}
                    onBlur={() => blurField('address_street')} />
                  {fieldError('address_street')}
                </div>
                <div className="form-field">
                  <label>{t('verification.common.postalCodeLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.address_postal_code} className={errClass('address_postal_code')}
                    onChange={(e) => setField('address_postal_code', e.target.value)}
                    onBlur={() => blurField('address_postal_code')} placeholder="1000" />
                  {fieldError('address_postal_code')}
                </div>
                <div className="form-field">
                  <label>{t('verification.common.cityLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.address_city} className={errClass('address_city')}
                    onChange={(e) => setField('address_city', e.target.value)}
                    onBlur={() => blurField('address_city')} />
                  {fieldError('address_city')}
                </div>
                <div className="form-field">
                  <label>{t('verification.common.countryLabel')}</label>
                  <select value={form.address_country} onChange={(e) => {
                    setForm(prev => ({ ...prev, address_country: e.target.value, address_region: '' }));
                  }}>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>{t('verification.step2.regionLabel')}</label>
                  {getRegions(countryIso(form.address_country)) ? (
                    <select value={form.address_region}
                      onChange={(e) => set('address_region', e.target.value)}>
                      <option value="">{t('verification.common.selectRegionPlaceholder')}</option>
                      {getRegions(countryIso(form.address_country)).map(r =>
                        <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={form.address_region}
                      onChange={(e) => set('address_region', e.target.value)}
                      placeholder={t('verification.step2.regionPlaceholder')} />
                  )}
                </div>
              </div>
            </div>

            <div className="form-field">
              <label className="checkbox-label">
                <input type="checkbox" checked={form.has_temp_address}
                  onChange={(e) => set('has_temp_address', e.target.checked)} />
                {t('verification.step2.hasTempAddressLabel')}
              </label>
            </div>

            {form.has_temp_address && (
              <div className="kyc-subsection">
                <h3>{t('verification.step2.tempAddressHeading')}</h3>
                <div className="form-grid">
                  <div className="form-field full-width">
                    <label>{t('verification.common.streetLabel')}</label>
                    <input type="text" value={form.temp_address_street}
                      onChange={(e) => set('temp_address_street', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>{t('verification.common.postalCodeLabel')}</label>
                    <input type="text" value={form.temp_address_postal_code}
                      onChange={(e) => set('temp_address_postal_code', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>{t('verification.common.cityLabel')}</label>
                    <input type="text" value={form.temp_address_city}
                      onChange={(e) => set('temp_address_city', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>{t('verification.common.countryLabel')}</label>
                    <select value={form.temp_address_country}
                      onChange={(e) => set('temp_address_country', e.target.value)}>
                      {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="kyc-subsection">
              <h3>{t('verification.step2.birthTaxHeading')}</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>{t('verification.common.birthDateLabel')} <span className="required">*</span></label>
                  <input type="text" inputMode="numeric" value={form.birth_date} className={errClass('birth_date')}
                    onChange={(e) => setField('birth_date', maskDateInput(e.target.value))}
                    onBlur={() => blurField('birth_date')}
                    placeholder={t('verification.common.dateFormatPlaceholder')} maxLength={10} />
                  {fieldError('birth_date')}
                </div>
                <div className="form-field">
                  <label>{t('verification.step2.birthplaceLabel')}</label>
                  <input type="text" value={form.birthplace}
                    onChange={(e) => set('birthplace', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>{t('verification.step2.birthCountryLabel')}</label>
                  <select value={form.birth_country} onChange={(e) => set('birth_country', e.target.value)}>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>{t('verification.step2.taxNumberLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.tax_number} className={errClass('tax_number')}
                    onChange={(e) => setField('tax_number', e.target.value)}
                    onBlur={() => blurField('tax_number')}
                    placeholder="SI12345678" />
                  {fieldError('tax_number')}
                </div>
                <div className="form-field">
                  <label>{t('verification.common.citizenshipLabel')} <span className="required">*</span></label>
                  <select value={form.citizenship}
                    onChange={(e) => set('citizenship', e.target.value)}>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
                {isExYugoslav(form.citizenship) && (
                  <div className="form-field">
                    <label>{t('verification.step2.personalIdLabel')} <span className="required">*</span></label>
                    <input type="text" value={form.emso} className={errClass('emso')}
                      onChange={(e) => setField('emso', e.target.value)}
                      onBlur={() => blurField('emso')}
                      placeholder={t('verification.step2.personalIdPlaceholder')} />
                    {fieldError('emso')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Identity, document, PEP */}
        {step === 3 && (
          <div className="kyc-section">
            <h2>{t('verification.step3.title')}</h2>

            <div className="kyc-subsection">
              <h3>{t('verification.step3.identityVerificationHeading')}</h3>
              {form.shufti_reference ? (
                <div className="alert alert-success">{t('verification.step3.identityVerifiedMessage')}</div>
              ) : (
                <ShuftiVerification
                  email={email}
                  onComplete={({ reference, extractedData }) => {
                    set('shufti_reference', reference);
                    if (extractedData) {
                      if (extractedData.id_number) {
                        setField('id_doc_number', extractedData.id_number);
                      }
                      if (extractedData.id_issue_date) {
                        setField('id_doc_issue_date', isoToDisplay(extractedData.id_issue_date));
                      }
                      if (extractedData.id_expiry_date) {
                        setField('id_doc_expiry_date', isoToDisplay(extractedData.id_expiry_date));
                      }
                    }
                  }}
                  onBack={() => {}}
                />
              )}
            </div>

            <div className="kyc-subsection">
              <h3>{t('verification.common.citizenshipLabel')}</h3>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.dual_citizenship}
                    onChange={(e) => set('dual_citizenship', e.target.checked)} />
                  {t('verification.step3.dualCitizenshipLabel')}
                </label>
              </div>
              {form.dual_citizenship && (
                <div className="form-field">
                  <label>{t('verification.step3.dualCitizenshipCountryLabel')}</label>
                  <select value={form.dual_citizenship_country}
                    onChange={(e) => set('dual_citizenship_country', e.target.value)}>
                    <option value="">{t('verification.common.selectPlaceholder')}</option>
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
                  </select>
                </div>
              )}
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.sanctions_connection}
                    onChange={(e) => set('sanctions_connection', e.target.checked)} />
                  {t('verification.step3.sanctionsConnectionLabel')}
                </label>
                <span className="field-hint">
                  <a href={SANCTIONS_LINK} target="_blank" rel="noopener noreferrer">
                    {t('verification.step3.sanctionsLinkText')}
                  </a>
                </span>
              </div>
            </div>

            <div className="kyc-subsection">
              <h3>{t('verification.step3.idDocumentHeading')}</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>{t('verification.step3.idDocTypeLabel')}</label>
                  <select value={form.id_doc_type} onChange={(e) => set('id_doc_type', e.target.value)}>
                    {ID_DOC_TYPES.map(docType => <option key={docType.value} value={docType.value}>{t(docType.labelKey)}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>{t('verification.step3.idDocNumberLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.id_doc_number} className={errClass('id_doc_number')}
                    onChange={(e) => setField('id_doc_number', e.target.value)}
                    onBlur={() => blurField('id_doc_number')} />
                  {fieldError('id_doc_number')}
                </div>
                <div className="form-field">
                  <label>{t('verification.step3.idDocIssuerLabel')} <span className="required">*</span></label>
                  <input type="text" value={form.id_doc_issuer} className={errClass('id_doc_issuer')}
                    onChange={(e) => setField('id_doc_issuer', e.target.value)}
                    onBlur={() => blurField('id_doc_issuer')} placeholder={t('verification.step3.idDocIssuerPlaceholder')} />
                  {fieldError('id_doc_issuer')}
                </div>
                <div className="form-field">
                  <label>{t('verification.step3.issueDateLabel')} <span className="required">*</span></label>
                  <input type="text" inputMode="numeric" value={form.id_doc_issue_date} className={errClass('id_doc_issue_date')}
                    onChange={(e) => setField('id_doc_issue_date', maskDateInput(e.target.value))}
                    onBlur={() => blurField('id_doc_issue_date')}
                    placeholder={t('verification.common.dateFormatPlaceholder')} maxLength={10} />
                  {fieldError('id_doc_issue_date')}
                </div>
                <div className="form-field">
                  <label>{t('verification.step3.expiryDateLabel')}</label>
                  <input type="text" inputMode="numeric" value={form.id_doc_expiry_date} className={errClass('id_doc_expiry_date')}
                    onChange={(e) => setField('id_doc_expiry_date', maskDateInput(e.target.value))}
                    onBlur={() => blurField('id_doc_expiry_date')}
                    placeholder={t('verification.common.dateFormatPlaceholder')} maxLength={10}
                    disabled={form.id_doc_permanent} />
                  {fieldError('id_doc_expiry_date')}
                </div>
              </div>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.id_doc_permanent}
                    onChange={(e) => set('id_doc_permanent', e.target.checked)} />
                  {t('verification.step3.permanentDocLabel')}
                </label>
              </div>
            </div>

            <div className="kyc-subsection">
              <h3>{t('verification.step3.pepStatusHeading')}</h3>
              <div className="form-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.pep_status}
                    onChange={(e) => set('pep_status', e.target.checked)} />
                  {t('verification.step3.pepStatusLabel')}
                </label>
                <button type="button" className="btn-text" onClick={() => setShowPepHelp(!showPepHelp)}>
                  {showPepHelp ? t('verification.step3.hidePepHelp') : t('verification.step3.whatIsPepButton')}
                </button>
                {showPepHelp && <p className="field-hint">{t('kyc.pepHelpText')}</p>}
              </div>
              {form.pep_status && (
                <div className="form-field">
                  <label>{t('verification.step3.pepDetailsLabel')}</label>
                  <input type="text" value={form.pep_details}
                    onChange={(e) => set('pep_details', e.target.value)} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Transaction context */}
        {step === 4 && (
          <div className="kyc-section">
            <h2>{t('verification.step4.title')}</h2>

            <div className="form-grid">
              <div className="form-field">
                <label>{t('verification.step4.buyVolumeLabel')} <span className="required">*</span></label>
                <input type="number" min="0" step="1000" value={form.buy_volume_per_year} className={errClass('buy_volume_per_year')}
                  onChange={(e) => setField('buy_volume_per_year', e.target.value)}
                  onBlur={() => blurField('buy_volume_per_year')} placeholder={t('verification.step4.buyVolumePlaceholder')} />
                {fieldError('buy_volume_per_year')}
              </div>
              <div className="form-field">
                <label>{t('verification.step4.purposeLabel')} <span className="required">*</span></label>
                <select value={form.purpose} className={errClass('purpose')}
                  onChange={(e) => setField('purpose', e.target.value)}
                  onBlur={() => blurField('purpose')}>
                  <option value="">{t('verification.common.selectPlaceholder')}</option>
                  {PURPOSES.map(p => <option key={p.value} value={p.value}>{t(p.labelKey)}</option>)}
                </select>
                {fieldError('purpose')}
              </div>
            </div>

            {form.purpose === 'ostalo' && (
              <div className="form-field">
                <label>{t('verification.step4.purposeOtherLabel')}</label>
                <input type="text" value={form.purpose_other}
                  onChange={(e) => set('purpose_other', e.target.value)} />
              </div>
            )}

            <div className="kyc-subsection">
              <h3>{t('verification.step4.sourceOfFundsHeading')} <span className="required">*</span></h3>
              <div className="checkbox-grid">
                {SOURCES_OF_FUNDS.map(s => (
                  <label key={s.value} className="checkbox-label">
                    <input type="checkbox" checked={form.source_of_funds.includes(s.value)}
                      onChange={() => toggleSource(s.value)} />
                    {t(s.labelKey)}
                  </label>
                ))}
              </div>
              {fieldError('source_of_funds')}
              {form.source_of_funds.includes('ostalo') && (
                <div className="form-field">
                  <label>{t('verification.step4.sourceOfFundsOtherLabel')}</label>
                  <input type="text" value={form.source_of_funds_other}
                    onChange={(e) => set('source_of_funds_other', e.target.value)} />
                </div>
              )}
              <div className="form-field">
                <label>
                  {t('verification.step4.sourceProofLabel')}
                  {isSourceProofRequired() ? (
                    <span className="required">*</span>
                  ) : (
                    <span className="optional">{t('verification.common.optionalMarker')}</span>
                  )}
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'source_proof')}
                  className={fieldErrors.source_proof ? 'input-error' : ''}
                />
                {fieldErrors.source_proof && (
                  <span className="field-error">{fieldErrors.source_proof}</span>
                )}
                {uploadMsg && <span className="field-hint">{uploadMsg}</span>}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-field">
                <label>{t('verification.step4.paymentMethodLabel')} <span className="required">*</span></label>
                <select value={form.payment_method} className={errClass('payment_method')}
                  onChange={(e) => setField('payment_method', e.target.value)}
                  onBlur={() => blurField('payment_method')}>
                  <option value="">{t('verification.common.selectPlaceholder')}</option>
                  {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{t(m.labelKey)}</option>)}
                </select>
                {fieldError('payment_method')}
              </div>
              <div className="form-field">
                <label>{t('verification.step4.recommendationLabel')}</label>
                <input type="text" value={form.recommendation_from}
                  onChange={(e) => set('recommendation_from', e.target.value)} />
              </div>
              <div className="form-field">
                <label>{t('verification.step4.howHeardLabel')}</label>
                <select value={form.heard_about_us} onChange={(e) => set('heard_about_us', e.target.value)}>
                  <option value="">{t('verification.common.selectPlaceholder')}</option>
                  {HOW_HEARD.map(h => <option key={h.value} value={h.value}>{t(h.labelKey)}</option>)}
                </select>
              </div>
            </div>

            {form.heard_about_us === 'ostalo' && (
              <div className="form-field">
                <label>{t('verification.step4.howHeardOtherLabel')}</label>
                <input type="text" value={form.heard_about_us_other}
                  onChange={(e) => set('heard_about_us_other', e.target.value)} />
              </div>
            )}

            <div className="form-field">
              <label>{t('verification.step4.commentLabel')}</label>
              <textarea value={form.customer_comment} rows="3"
                onChange={(e) => set('customer_comment', e.target.value)} />
            </div>
          </div>
        )}

        <div className="kyc-buttons">
          {step > 1 && (
            <button type="button" className="btn btn-secondary" onClick={back}>{t('common.back')}</button>
          )}
          {step < 4 ? (
            <button type="button" className="btn btn-primary" onClick={next}>{t('common.next')}</button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('verification.step4.savingButton') : t('verification.step4.submitButton')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default Verification;
